import { useId, type ChangeEvent } from 'react';
import { MFA_CODE_LENGTH } from '@constants/mfa';
import styles from './styles.module.css';

interface IMfaCodeInputProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  /**
   * Fired the moment the field holds a full-length code — whether that came
   * from the last keystroke or from a paste. Lets the caller submit without the
   * user reaching for the button, which is the whole point of a 6-digit field.
   */
  onComplete?: (code: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  hint?: string;
}

/**
 * One-time-code entry. Deliberately a SINGLE input rather than N boxes: a
 * single field pastes cleanly (the email-OTP path is copy-paste-first), gets
 * the platform's `one-time-code` autofill for free, and stays one focus target
 * for screen readers and gloved-hand mobile use. The segmented look is purely
 * `letter-spacing` on a mono face.
 *
 * Non-digits are dropped on input, so pasting "123 456" or "code: 123456"
 * still lands a clean code.
 */
export function MfaCodeInput({
  label,
  value,
  onChange,
  onComplete,
  length = MFA_CODE_LENGTH,
  disabled = false,
  autoFocus = false,
  hint,
}: IMfaCodeInputProps) {
  const hintId = useId();

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const next = e.target.value.replace(/\D/g, '').slice(0, length);
    if (next === value) return;
    onChange(next);
    if (next.length === length) onComplete?.(next);
  }

  return (
    <label className={styles.field}>
      <span className={styles.label}>{label}</span>
      <input
        className={styles.input}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        // A 6-digit field with a spellcheck squiggle looks broken.
        spellCheck={false}
        pattern="[0-9]*"
        maxLength={length}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-describedby={hint ? hintId : undefined}
        required
      />
      {hint ? (
        <small className={styles.hint} id={hintId}>
          {hint}
        </small>
      ) : null}
    </label>
  );
}
